import type { HyperdxClient, HyperdxInitConfig } from '../integrations/hyperdx';
import type { Logger, LoggerRole } from '../logger';
import type { Attributes } from '../types';

import {
  browserHyperdxClient,
  withDefaultHyperdxWorker,
} from './HyperdxBrowserBinding';

export type {
  HyperdxBaseConfig,
  HyperdxClient,
  HyperdxInitConfig,
  HyperdxMode,
} from '../integrations/hyperdx';

export interface HyperdxLoggerOptions {
  role?: LoggerRole;
  /**
   * HyperDX client 주입.
   * 기본값은 browser singleton(`browserHyperdxClient`)입니다.
   * 테스트나 worker 환경에서 SDK singleton을 우회할 때 명시합니다.
   */
  client?: HyperdxClient;
}

/**
 * HyperDX 로거.
 *
 * 앱별 logger.ts 조립 파일에서 `new HyperdxLogger(config)` 형식으로 사용합니다.
 * 기본은 browser binding(SDK singleton + default worker factory)을 주입합니다.
 *
 * 사용법:
 *   new HyperdxLogger({ service: 'app' })
 *   new HyperdxLogger({ service: 'app', mode: 'worker' })
 *   new HyperdxLogger({ service: 'app', mode: 'worker', workerFactory })
 *   new HyperdxLogger({ role: 'debug' })                       // role-only, init 미발생
 *   new HyperdxLogger(config, { client: mockClient })          // DI
 *
 * 기본 role은 'all'이며 tracking/debug/error를 모두 수신합니다.
 */
export class HyperdxLogger implements Logger {
  public name = 'hyperdx';
  public role: LoggerRole;
  private readonly _client: HyperdxClient;
  private readonly _initConfig?: HyperdxInitConfig;

  constructor(
    configOrOptions?: HyperdxInitConfig | { role?: LoggerRole },
    options?: HyperdxLoggerOptions,
  ) {
    this._client = options?.client ?? browserHyperdxClient;

    // Browser default worker factory 주입은 client가 명시되지 않은 경우에만 적용.
    // DI 시점에는 caller가 전달한 config를 그대로 사용해 결정 가능성 유지.
    const effective = options?.client
      ? configOrOptions
      : withDefaultHyperdxWorker(configOrOptions);

    const isConfig =
      effective !== undefined &&
      'service' in (effective as Record<string, unknown>);
    this._initConfig = isConfig ? (effective as HyperdxInitConfig) : undefined;
    const roleOpt = isConfig
      ? options
      : (effective as { role?: LoggerRole } | undefined);
    this.role = roleOpt?.role ?? 'all';

    if (this._initConfig) {
      void this._client.init(this._initConfig);
    }
  }

  log: Logger['log'] = (message, params) => {
    this._client.logDirect(message, params ?? {});
  };

  track: Logger['track'] = (message, params) => {
    this._client.log(message, params ?? {});
  };

  setState: Logger['setState'] = state => {
    if (state) {
      this._client.setGlobalAttributes(state as Attributes);
    }
  };

  debug: Logger['debug'] = (message, params) => {
    this._client.logDirect(message, params ?? {});
  };

  captureError: Logger['captureError'] = (error, options) => {
    const merged: Record<string, unknown> = {
      ...(options?.attributes ?? {}),
      ...(options?.extra ?? {}),
      ...(options?.tags ?? {}),
    };
    if (options?.level) {
      merged.level = options.level;
    }
    if (options?.fingerprint) {
      merged.fingerprint = options.fingerprint;
    }
    this._client.captureException(error as Error, merged);
  };
}
